import React, { forwardRef } from 'react';

import { Text } from './Text';

type Props = Omit<
  React.ComponentProps<typeof Text>,
  'fontSize' | 'fontWeight' | 'as'
>;

/**
 * Top level header for a page or drawer.
 * Resolves to a `<h1>` with 20px font size, bold.
 */
export const PageHeader = forwardRef<HTMLParagraphElement, Props>(
  (props, ref) => (
    <Text {...props} fontSize={20} fontWeight="bold" as="h1" ref={ref} />
  )
);

/**
 * Header for a Tab section (both in a page and in a drawer).
 * Resolves to a `<h2>` with 20px font size, bold.
 */
export const TabHeader = forwardRef<HTMLParagraphElement, Props>(
  (props, ref) => (
    <Text {...props} fontSize={20} fontWeight="bold" as="h2" ref={ref} />
  )
);

/**
 * Header for a section in a page or Drawer.
 * Resolves to a `<h2>` with 16px font size, bold.
 */
export const SectionHeader = forwardRef<HTMLParagraphElement, Props>(
  (props, ref) => (
    <Text {...props} fontSize={16} fontWeight="bold" as="h2" ref={ref} />
  )
);

/**
 * E.g. Header for KPI, SE, ... Cards.
 * Resolves to a `<h3>` with 16px font size, bold.
 */
export const CardHeader = forwardRef<HTMLParagraphElement, Props>(
  (props, ref) => (
    <Text {...props} fontSize={16} fontWeight="bold" as="h3" ref={ref} />
  )
);

/**
 * To be used for long paragraphs of text, for example the Strategic Element description.
 * Resolves to a `<p>` with 14px font size, regular, and grey-dark color.
 */
export const Description = forwardRef<HTMLParagraphElement, Props>(
  (props, ref) => (
    <Text
      {...props}
      fontSize={14}
      fontWeight="regular"
      as="p"
      ref={ref}
      color="grey-dark"
    />
  )
);

/**
 * To be used for small titles and UI elements (buttons, labels, etc.).
 * Resolves to a `<h4>` with 14px font size, bold.
 */
export const SmallTitle = forwardRef<HTMLParagraphElement, Props>(
  (props, ref) => (
    <Text {...props} fontSize={14} fontWeight="bold" as="h4" ref={ref} />
  )
);

/**
 * To be used for supporting small text, for example the supporting text below a form field.
 * Resolves to a `<p>` with 12px font size, regular, and grey-dark color.
 */
export const SmallDescription = forwardRef<HTMLParagraphElement, Props>(
  (props, ref) => (
    <Text
      {...props}
      fontSize={12}
      fontWeight="regular"
      as="p"
      ref={ref}
      color="grey-dark"
    />
  )
);
